import * as Api from '@ViewModels';

/**
 * Presentation-agnostic taxonomy for the points-based cold-call adherence rubric: id→label maps,
 * process-area caps, per-trigger point weights, call-type multipliers, and outcome-tier points.
 * Mirrors the backend `CoachingRubricBreakdown` scorer so the review UI can render `firedTriggers` /
 * `negativeModifiers` / `disqualifiers` (snake_case ids), `outcomeTier` / `callType` (PascalCase
 * enums), and the six per-area scores without re-deriving labels. Pure data — no colors or layout.
 *
 * The per-trigger `points`, call-type `multiplier`, and modifier/disqualifier `effect` values are
 * NOT delivered by the API (it sends only which triggers fired and the per-area earned score) —
 * they are static rubric weights shown for explanation, and must be kept in sync with the backend
 * scorer spec if it re-weights.
 */

export const SALES_CALL_STEP_LABELS: Record<Api.SalesCallStep, string> = {
	Unknown: 'Unknown',
	GetToDm: 'Get to DM',
	Hook: 'Hook',
	Pitch: 'Pitch',
	QualifyingQuestions: 'Qualifying Questions',
	Close: 'Close',
	Other: 'Other',
	PostDemoQualify: 'Post-Demo Qualify',
};

type PerAreaScoreKey =
	| 'getToDmScore'
	| 'hookScore'
	| 'pitchScore'
	| 'qualifyingQuestionsScore'
	| 'closeScore'
	| 'postDemoQualifyScore';

export interface IGoalTrigger {
	id: string;
	label: string;
	points: number;
}

export interface IProcessArea {
	step: Api.SalesCallStep;
	/** Rubric-area display name (distinct from the generic step name in {@link SALES_CALL_STEP_LABELS}). */
	label: string;
	/** Max process points this area contributes (process areas sum to 70). */
	cap: number;
	scoreKey: PerAreaScoreKey;
	triggers: readonly IGoalTrigger[];
}

export const PROCESS_AREAS: readonly IProcessArea[] = [
	{
		step: 'GetToDm',
		label: 'Get to the DM',
		cap: 10,
		scoreKey: 'getToDmScore',
		triggers: [
			{ id: 'reached_dm', label: 'Reached the DM (live)', points: 5 },
			{ id: 'cleared_gatekeeper', label: 'Cleared gatekeeper w/ credibility signal', points: 3 },
			{ id: 'secured_path_when_unavailable', label: 'Secured a path when DM unavailable', points: 3 },
			{ id: 'engaged_gatekeeper_as_dm', label: 'Engaged gatekeeper as potential DM', points: 2 },
		],
	},
	{
		step: 'Hook',
		label: 'The Hook',
		cap: 10,
		scoreKey: 'hookScore',
		triggers: [
			{ id: 'asked_permission', label: 'Asked permission crisply', points: 2 },
			{ id: 'industry_pain_hook', label: 'Led with industry pain before product', points: 4 },
			{ id: 'value_framing', label: 'Framed value (time/personal vs mass)', points: 3 },
			{ id: 'maintained_momentum', label: 'Kept momentum, no easy-exit re-hook', points: 2 },
		],
	},
	{
		step: 'Pitch',
		label: 'The Pitch',
		cap: 12,
		scoreKey: 'pitchScore',
		triggers: [
			{ id: 'complete_pitch', label: 'Delivered a complete pitch', points: 5 },
			{ id: 'positioned_alongside', label: 'Positioned alongside existing systems', points: 2 },
			{ id: 'differentiated_category', label: 'Differentiated from category', points: 2 },
			{ id: 'diagnostic_question', label: 'Asked a diagnostic/current-state Q', points: 2 },
			{ id: 'pitch_closed_with_next_step', label: 'Closed pitch with a next step', points: 3 },
		],
	},
	{
		step: 'QualifyingQuestions',
		label: 'RBOs & Qualifying',
		cap: 13,
		scoreKey: 'qualifyingQuestionsScore',
		triggers: [
			{ id: 'acknowledged_and_pivoted', label: 'Acknowledged objection, pivoted to discovery', points: 4 },
			{ id: 'discovery_q_1', label: 'Open-ended discovery question', points: 3 },
			{ id: 'discovery_q_2', label: '2nd distinct discovery question', points: 2 },
			{ id: 'objection_to_discovery', label: 'Converted objection into discovery', points: 3 },
			{ id: 'recognized_disqualification', label: 'Recognized a fit/DQ signal', points: 3 },
		],
	},
	{
		step: 'Close',
		label: 'Hard Roll to Close',
		cap: 15,
		scoreKey: 'closeScore',
		triggers: [
			{ id: 'assumptive_language', label: 'Used assumptive language', points: 4 },
			{ id: 'specific_times_offered', label: 'Offered specific, limited times', points: 4 },
			{ id: 'concrete_duration', label: 'Set concrete credible duration', points: 2 },
			{ id: 'locked_in', label: 'Locked it in on the call', points: 3 },
			{ id: 'firm_commitment', label: 'Secured a firm commitment', points: 2 },
		],
	},
	{
		step: 'PostDemoQualify',
		label: 'Post-Demo Qualify',
		cap: 10,
		scoreKey: 'postDemoQualifyScore',
		triggers: [
			{ id: 'post_demo_tailoring', label: 'Tailoring discovery after booking', points: 4 },
			{ id: 'captured_process_stack', label: 'Captured current process / tech stack', points: 3 },
			{ id: 'kept_brief', label: 'Kept it brief / offered email alt', points: 3 },
		],
	},
];

/** The reduced Pitch-area cap applied when the scorer flags a sub-product-only pitch. */
export const SUB_PRODUCT_PITCH_CAP = 6;
export const SUB_PRODUCT_PITCH_FLAG = 'sub_product_pitch_capped';

export const GOAL_TRIGGER_LABELS: Record<string, string> = (() => {
	const map: Record<string, string> = {};
	for (const area of PROCESS_AREAS) {
		for (const trigger of area.triggers) {
			map[trigger.id] = trigger.label;
		}
	}
	return map;
})();

export interface ICallTypeMeta {
	type: Api.ColdCallType;
	label: string;
	/** Attribution multiplier applied to the outcome points for this call type. */
	multiplier: number;
}

/** Call types shown as the attribution chip row, richest first. Excludes `Unknown` (not a chip). */
export const CALL_TYPES: readonly ICallTypeMeta[] = [
	{ type: Api.ColdCallType.FreshColdCall, label: 'Fresh cold call', multiplier: 1.0 },
	{ type: Api.ColdCallType.WarmInbound, label: 'Warm inbound', multiplier: 0.8 },
	{ type: Api.ColdCallType.ReEngagement, label: 'Re-engagement', multiplier: 0.6 },
	{ type: Api.ColdCallType.FollowUp, label: 'Follow-up', multiplier: 0.4 },
	{ type: Api.ColdCallType.RescheduleOrConfirmation, label: 'Reschedule / confirmation', multiplier: 0.15 },
];

export const CALL_TYPE_LABELS: Record<Api.ColdCallType, string> = {
	[Api.ColdCallType.Unknown]: 'Unknown',
	[Api.ColdCallType.FreshColdCall]: 'Fresh cold call',
	[Api.ColdCallType.WarmInbound]: 'Warm inbound',
	[Api.ColdCallType.ReEngagement]: 'Re-engagement',
	[Api.ColdCallType.FollowUp]: 'Follow-up',
	[Api.ColdCallType.RescheduleOrConfirmation]: 'Reschedule / confirmation',
};

/** Call types whose outcome is discounted by a new-work score (worth surfacing that math). */
export const NEW_WORK_CALL_TYPES: ReadonlySet<Api.ColdCallType> = new Set([
	Api.ColdCallType.FollowUp,
	Api.ColdCallType.ReEngagement,
]);

export interface IOutcomeTierMeta {
	tier: Api.ColdCallOutcomeTier;
	label: string;
	points: number;
}

/** Outcome tiers in descending point order (the single highest achieved tier drives the outcome). */
export const OUTCOME_TIERS: readonly IOutcomeTierMeta[] = [
	{ tier: Api.ColdCallOutcomeTier.BookedDemoPrequalified, label: 'Booked demo + pre-qualified', points: 30 },
	{ tier: Api.ColdCallOutcomeTier.BookedDemo, label: 'Booked demo (not qualified)', points: 24 },
	{ tier: Api.ColdCallOutcomeTier.FirmScheduledCallback, label: 'Firm scheduled callback', points: 16 },
	{ tier: Api.ColdCallOutcomeTier.ReferralOrTransferToDm, label: 'Referral / transfer to DM', points: 14 },
	{ tier: Api.ColdCallOutcomeTier.CleanDisqualification, label: 'Clean disqualification', points: 13 },
	{ tier: Api.ColdCallOutcomeTier.ReachedDmAndAdvanced, label: 'Reached DM + advanced', points: 10 },
	{ tier: Api.ColdCallOutcomeTier.NewContactChannelObtained, label: 'New contact channel obtained', points: 8 },
	{ tier: Api.ColdCallOutcomeTier.OkToSendInfo, label: 'OK to send info + next touch', points: 4 },
	{ tier: Api.ColdCallOutcomeTier.NoAdvance, label: 'No advance / no outcome', points: 0 },
];

export const OUTCOME_TIER_META = OUTCOME_TIERS.reduce<Partial<Record<Api.ColdCallOutcomeTier, IOutcomeTierMeta>>>(
	(acc, meta) => {
		acc[meta.tier] = meta;
		return acc;
	},
	{}
);

export interface INegativeModifierMeta {
	label: string;
	effect: string;
	/** Severe modifiers meaningfully move the score (cap/penalty); the rest are review flags. */
	severe: boolean;
}

export const NEGATIVE_MODIFIERS: Record<string, INegativeModifierMeta> = {
	previous_client: { label: 'Previous client / existing relationship', effect: 'caps ×0.25', severe: true },
	sub_product_pitch_only: { label: 'Only pitched a sub-product (e.g. website)', effect: 'caps Pitch 6', severe: true },
	inbound_warm_lead: { label: 'Inbound / warm lead', effect: 'review flag', severe: false },
	gatekeeper_only: { label: 'Never reached the DM (gatekeeper only)', effect: 'review flag', severe: false },
	follow_up_call: { label: 'Follow-up (kept for review)', effect: 'review flag', severe: false },
	reschedule_or_confirm_only: { label: 'Reschedule / confirm only', effect: 'review flag', severe: false },
	truncated_or_cut_short: { label: 'Call truncated / cut short', effect: 'review flag', severe: false },
	wrong_number_or_contact: { label: 'Wrong number / contact', effect: 'review flag', severe: false },
	rambling_delivery: { label: 'Rambling delivery', effect: 'review flag', severe: false },
};

/** Tier A forces the score to 0; Tier B applies a penalty or cap. */
export type DisqualifierTier = 'A' | 'B';

export interface IDisqualifierMeta {
	id: string;
	label: string;
	tier: DisqualifierTier;
	effect: string;
}

export const DISQUALIFIERS: readonly IDisqualifierMeta[] = [
	{ id: 'dnc_ignored', label: 'Continued after Do-Not-Call / opt-out', tier: 'A', effect: 'Forces 0' },
	{ id: 'abusive_to_prospect', label: 'Abuse / deception toward prospect', tier: 'A', effect: 'Forces 0' },
	{ id: 'compliance_issue', label: 'Compliance violation', tier: 'A', effect: 'Forces 0' },
	{ id: 'pushed_misfit', label: 'Pushed a misfit', tier: 'B', effect: 'Caps at 50' },
	{ id: 'argued_self_assessment', label: 'Argued with self-assessment', tier: 'B', effect: '−3' },
	{ id: 'mild_rudeness', label: 'Mild rudeness', tier: 'B', effect: '−3' },
	{ id: 'unprofessional_conduct', label: 'Unprofessional conduct', tier: 'B', effect: '−3' },
];

export const DISQUALIFIER_META = DISQUALIFIERS.reduce<Record<string, IDisqualifierMeta>>((acc, meta) => {
	acc[meta.id] = meta;
	return acc;
}, {});
